import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Card from 'components/Layout/Card';
import Table from 'components/Layout/Table';
import CheckBox from 'components/form/CheckBox';
import { fetchResourceAllocation, fetchResourceData } from 'store/slices';
import {
  objectToFormData,
  titleCaseString,
  userTypeByStatus,
  getDataFromLocalStorage,
} from 'utils/helpers';
import { limit } from 'utils/constants';
import './ResourceAllocation.scss';
import AllocateDropdown from 'components/V2ForNewAbstractFlow/ReviewerSide/ReviewerPaperStatusFilter/AllocateMenu';
import SeachInput from 'components/form/SeachInput';

const ResourceAllocation = ({ eventId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [tableList, setTableList] = useState([]);
  const [resourceListOptions, setResourceListOptions] = useState([]);
  const [resourceList, setResourceList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isCheckAll, setIsCheckAll] = useState(false);
  const [isReAllocation, setIsReAllocation] = useState(false);
  const [globalSearchVal, setGlobalSearchVal] = useState('');
  const [timer, setTimer] = useState('');

  const [searchPayload, setSearchPayload] = useState({
    author_name: '',
    paper_id: '',
    paper_title: '',
    email_id: '',
    resource: '',
  });

  const [filterData, setFilterData] = useState({
    total: 0,
    offset: 0,
    limit,
    author_name: '',
    event_id: eventId || '',
  });

  const { userDetails } = useSelector((state) => ({
    userDetails: state.student.userDetails,
  }));
  
  const permissionMapping = {
    'event-management': 'Event Management',
    'chapters-groups': 'Chapters & Group Management',
  };
  const path = window.location.pathname;
  const segments = path.split('/');
  const targetValue = segments[2] || '';
  const permissionName = permissionMapping[targetValue] || '';

  const {
    // view: viewAccess,
    update: updateAccess,
    // delete: deleteAccess,
    // create: createAccess,
  } =
    userDetails?.role_data?.find(
      (item) => item?.permission_name === permissionName
    )?.sub_permission || {};

  useEffect(() => {
    const fetchData = async () => {
      const response = await dispatch(fetchResourceData({ event_id: eventId }));
      if (response?.data?.resource) {
        setResourceList(response?.data?.resource);
        setResourceListOptions(
          response.data.resource.map(({ resource_name }) => ({
            name: resource_name,
            value: resource_name,
          }))
        );
      }
    };
    fetchData();
  }, [eventId, dispatch]);

  useEffect(() => {
    getTableList({ ...searchPayload, ...filterData });
  }, []);

  useEffect(() => {
    if (isReAllocation) {
      setSelectedIds([]);
      setIsCheckAll(false);
      getTableList({ ...searchPayload, ...filterData });
    }
  }, [isReAllocation]);

  const getTableList = async (payload) => {
    setIsLoading(true);
    const response = await dispatch(
      fetchResourceAllocation(objectToFormData(payload))
    );
    if (response?.data?.abstracts) {
      setTableList(response.data.abstracts);
      setFilterData((prev) => ({
        ...prev,
        total: response.data.result_count || 0,
      }));
    }
    setIsLoading(false);
  };

  const handleSearchChange = (searchData) => {
    setSearchPayload(searchData);
    const updatedData = { ...filterData, ...searchData, offset: 0 };
    setFilterData(updatedData);
    getTableList(updatedData);
  };

  const handleGlobalSearch = (e) => {
    setGlobalSearchVal(e.target.value);
    let time = timer;
    clearTimeout(time);
    time = setTimeout(() => {
      let newData = { ...filterData, ...searchPayload };
      newData = { ...newData, author_name: e.target.value, offset: 0 };
      setIsLoading(true);
      setFilterData(newData);
      getTableList(newData);
    }, 800);
    setTimer(time);
  };

  const handlePaginationChange = (offset) => {
    setSelectedIds([])
    setIsCheckAll(false)
    setFilterData((prev) => ({ ...prev, offset }));
    getTableList({ ...searchPayload, ...filterData, offset });
  };

  const handleSelectAllChange = () => {
    setIsCheckAll((prev) => !prev);
    setSelectedIds(isCheckAll ? [] : tableList.map(({ id }) => id));
  };

  //   const handleCheckboxChange = (id, isChecked) => {
  //     setSelectedIds((prev) =>
  //       isChecked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id)
  //     );
  //   };

  const handleCheckboxChange = (id, isChecked) => {
    setSelectedIds((prev) => {
      const updatedSelectedIds = isChecked
        ? [...prev, id]
        : prev.filter((selectedId) => selectedId !== id);

      // Check if all rows are selected
      setIsCheckAll(updatedSelectedIds.length === tableList.length);

      return updatedSelectedIds;
    });
  };

  const memberType = userTypeByStatus(getDataFromLocalStorage('user_type'));
  const handleRedirect = (paperId) =>
    navigate(`/${memberType}/event-management/new-submitted-papers/${paperId}`);

  const header = [
    { isSearch: false },
    {
      isSearch: true,
      searchInputName: 'author_name',
      title: <div className="text-nowrap">Author Name</div>,
    },
    {
      isSearch: true,
      searchInputName: 'email_id',
      title: <div className="text-nowrap">Email</div>,
    },
    { isSearch: true, searchInputName: 'paper_id', title: 'Paper ID' },
    { isSearch: true, searchInputName: 'paper_title', title: 'Paper Title' },
    {
      isSearch: true,
      isReviewerStatusFilter: true,
      isMyTaskFilter: true,
      myTaskDropdownOptions: {
        options: resourceListOptions,
        key: 'name',
        value: 'value',
      },
      handleStatus: (e) => {
        setSearchPayload((prev) => {
          return { ...prev, resource: e.target.value };
        });
      },
      searchInputName: 'resource',
      title: <span className="text-nowrap">Resource</span>,
    },
    { isSearch: false, title: 'Action' },
  ];

  const rowData = tableList.map(
    ({ id, author_name, email_id, paper_id, paper_title, resource }) => ({
      data: [
        {
          value: (
            <input
              type="checkbox"
              className={`custom-checkbox ${
                selectedIds.includes(id) ? 'activeChecked' : 'activeUnchecked'
              }`}
              onChange={(e) => handleCheckboxChange(id, e.target.checked)}
              checked={selectedIds.includes(id)}
            />
          ),
        },
        { value: <span>{author_name}</span> },
        { value: <div className="color-new-car text-nowrap">{email_id}</div> },
        {
          value: (
            <span
              className="pointer color-new-car text-nowrap"
              onClick={() => handleRedirect(id)}
            >
              {paper_id}
            </span>
          ),
        },
        {
          value: (
            <span className="text-wrap">{titleCaseString(paper_title)}</span>
          ),
        },
        { value: <span className="text-wrap">{resource || '-'}</span> },
        {
          value: (
            <span className="action-icon-buttons">
              <i
                className="bi bi-eye pointer color-new-car"
                onClick={() => handleRedirect(id)}
              />
            </span>
          ),
        },
      ],
    })
  );

  return (
    <Card className="cps-20 cpe-20 cpb-20 cmt-10">
      <div className="d-flex justify-content-between align-items-center cpt-28 cpb-28 flex-wrap gap-2">
        {selectedIds.length === 0 ? (
          <div className="table-title">
            Allocated Resource ({filterData.total})
          </div>
        ) : (
          <>
            <div className="d-flex gap-2">
              <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
                <CheckBox
                  type="ACTIVE"
                  isChecked={isCheckAll}
                  onClick={handleSelectAllChange}
                />
                <span>Select All Resource</span>
              </div>
              {updateAccess === '1' && (
                <AllocateDropdown
                  resourceList={resourceList}
                  selectedIds={selectedIds}
                  eventId={eventId}
                  setIsReAllocation={setIsReAllocation}
                />
              )}
            </div>
          </>
        )}

        <div className="d-flex">
          <SeachInput
            placeholder="Search here"
            id="global_filter"
            value={globalSearchVal}
            onChange={handleGlobalSearch}
          />
        </div>
      </div>
      <Table
        isLoading={isLoading}
        header={header}
        rowData={rowData}
        filterData={filterData}
        searchPayload={searchPayload}
        searchInputChange={handleSearchChange}
        changeOffset={handlePaginationChange}
      />
    </Card>
  );
};

export default ResourceAllocation;
